export const PAGE_SIZE = 35;
export const SEARCH_TIMEOUT = 8000;
export const FACETS = ['collection', 'category', 'stone', 'metal', 'color'];
export const SORTS = ['manual', 'created-descending', 'price-ascending', 'price-descending'];

const pageNumber = value => /^\d+$/.test(value || '') && Number.isSafeInteger(Number(value)) && Number(value) > 0 && Number(value) <= Math.floor(Number.MAX_SAFE_INTEGER / PAGE_SIZE) ? Number(value) : 1;

export function readSearchState(url) {
  const params = new URL(url).searchParams;
  return {
    query: params.get('q') || '',
    page: pageNumber(params.get('page')),
    contentPage: pageNumber(params.get('content_page')),
    sort: SORTS.includes(params.get('sort_by')) ? params.get('sort_by') : 'manual',
    filters: Object.fromEntries(FACETS.map(group => [group, [...new Set(params.getAll(`filter.${group}`).filter(Boolean))]])),
  };
}

export function usesNosto(url, suppressed = false) {
  return !suppressed && new URL(url).searchParams.get('search_provider') !== 'shopify' && Boolean(readSearchState(url).query.trim());
}

export function searchRequest(state) {
  const sorts = {
    'created-descending': { field: 'datePublished', order: 'desc' },
    'price-ascending': { field: 'price', order: 'asc' },
    'price-descending': { field: 'price', order: 'desc' },
  };
  return {
    query: state.query,
    products: {
      fields: ['productId', 'name', 'url'],
      size: PAGE_SIZE,
      from: (state.page - 1) * PAGE_SIZE,
      facets: ['*'],
      filter: FACETS.filter(group => state.filters[group]?.length).map(group => ({ field: `extra.${group}`, value: state.filters[group] })),
      ...(sorts[state.sort] ? { sort: [sorts[state.sort]] } : {}),
    },
  };
}

export function updateSearchUrl(current, changes) {
  const url = new URL(current);
  if (changes.filters) {
    FACETS.forEach(group => {
      url.searchParams.delete(`filter.${group}`);
      (changes.filters[group] || []).forEach(value => url.searchParams.append(`filter.${group}`, value));
    });
  }
  if (changes.sort) url.searchParams.set('sort_by', changes.sort);
  if (changes.filters || changes.sort) url.searchParams.delete('page');
  if (changes.page !== undefined) url.searchParams.set('page', changes.page);
  if (changes.contentPage !== undefined) url.searchParams.set('content_page', changes.contentPage);
  return url;
}

export function freshSearchUrl(current, action, query) {
  const url = new URL(current);
  url.pathname = new URL(action, current).pathname;
  url.hash = '';
  ['q', 'page', 'content_page', 'sort_by', 'search_provider', 'type', 'view', ...FACETS.map(group => `filter.${group}`)].forEach(key => url.searchParams.delete(key));
  url.searchParams.set('q', query);
  return url;
}

export function fallbackUrl(current) {
  const url = new URL(current);
  ['page', 'content_page', 'type', 'view', ...FACETS.map(group => `filter.${group}`)].forEach(key => url.searchParams.delete(key));
  if (!SORTS.includes(url.searchParams.get('sort_by'))) url.searchParams.delete('sort_by');
  url.searchParams.set('search_provider', 'shopify');
  return url;
}

export function fragmentUrl(current, action, view, query, page = 1) {
  const url = freshSearchUrl(current, action, query);
  url.searchParams.set('view', view);
  url.searchParams.set('type', view === 'nosto-content' ? 'article,page' : 'product');
  url.searchParams.set('page', page);
  return url;
}

export function validateProducts(response) {
  const products = response?.products;
  if (!products || !Number.isSafeInteger(products.total) || products.total < 0 || !Array.isArray(products.hits) || (products.total === 0 && products.hits.length)) {
    throw new Error('Invalid Nosto search response');
  }
  return products;
}

export function productHandle(hit, current) {
  const url = new URL(hit.url, current);
  const handle = url.pathname.match(/\/products\/([a-z0-9-]+)\/?$/)?.[1];
  if (!handle || !/^\d+$/.test(String(hit.productId))) throw new Error('Invalid Nosto product');
  return handle;
}

export function withTimeout(promise, milliseconds = SEARCH_TIMEOUT) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Search timed out')), milliseconds); }),
  ]).finally(() => clearTimeout(timer));
}

export function latestRequest() {
  let sequence = 0;
  let controller;
  return () => {
    controller?.abort();
    controller = new AbortController();
    const id = ++sequence;
    return { signal: controller.signal, isCurrent: () => id === sequence };
  };
}
